import { Dispatch, SetStateAction, useEffect } from 'react';
import { useLazyLoadQuery } from 'react-relay';
import { getSkillAssessments } from '../../graphQLOperations/Monitoring';
import { MonitoringSkillAssessmentsQuery } from '../../graphQLOperations/__generated__/MonitoringSkillAssessmentsQuery.graphql';
import { SkillAssessmentTableFilters, SkillAssessmentTableItem } from './SkillAssessmentTable';
import { PageInfo } from '../../models/PageInfo';
import { sortOrderCodeForQueries } from '../../businessLogicService/sortOrderCodeForQueries';
import { getDateFiltersForQueries } from '../../utils/getDateFiltersForQueries';

export interface QuerySkillAssessmentsDataProp {
  filterState: SkillAssessmentTableFilters;
  setFetchingData: Dispatch<SetStateAction<boolean>>;
  setSkillAssessments: Dispatch<SetStateAction<SkillAssessmentTableItem[]>>;
  setTotalRecords: Dispatch<SetStateAction<number>>;
  setPageInfo: Dispatch<SetStateAction<PageInfo | undefined>>;
  pageInfo: PageInfo | undefined;
  fetchingData: boolean;
  firstLoad: boolean;
  setFirstLoad: Dispatch<SetStateAction<boolean>>;
}

export const AssessmentHistoryQueryData = ({
  setFetchingData,
  setSkillAssessments,
  filterState,
  setTotalRecords,
  setPageInfo,
  pageInfo,
  fetchingData,
  firstLoad,
  setFirstLoad,
}: QuerySkillAssessmentsDataProp) => {
  const isMovingNext = filterState.page! - filterState.prevPage === 1;
  const isMovingPrevious = filterState.page! - filterState.prevPage === -1;
  const isNotMoving = filterState.page! - filterState.prevPage === 0;
  const isSkillCode = filterState.filters.skillNameCode?.split('').every(char => !isNaN(Number(char)));

  const { startDateAndTime, endDateAndTime } = getDateFiltersForQueries(filterState.filters.completedOn);

  const skillAsssessmentData = useLazyLoadQuery<MonitoringSkillAssessmentsQuery>(
    getSkillAssessments,
    {
      filter: {
        classIds: filterState.filters.classes,
        targetGroupIds: filterState.filters.roadmaps,
        standardGradeIds: filterState.filters.skillGrades,
        gradeLevelIds: filterState.filters.gradeLevels,
        studentName: filterState.filters.studentName,
        skillName: !isSkillCode ? filterState.filters.skillNameCode : null,
        skillCode: isSkillCode ? filterState.filters.skillNameCode : null,
        assessmentScores: filterState.filters.score,
        assessmentCompletedFrom: startDateAndTime ? startDateAndTime : null,
        assessmentCompletedTo: endDateAndTime ? endDateAndTime : null,
      },
      first: fetchingData && (isMovingNext || isNotMoving || !pageInfo?.hasPreviousPage) ? filterState.rows : null,
      last: fetchingData && isMovingPrevious && pageInfo?.hasPreviousPage ? filterState.rows : null,
      order: [
        filterState.sortOrder === 1
          ? {
              user: {
                lastName: sortOrderCodeForQueries(filterState.sortOrder),
                firstName: sortOrderCodeForQueries(filterState.sortOrder),
              },
              ...(filterState.sortBySkill !== false && { skill: { id: 'ASC' } }),
              sessionNumber: sortOrderCodeForQueries(filterState.sortOrderAttempted),
              sessionCompletedOn: 'DESC',
            }
          : {
              sessionNumber: sortOrderCodeForQueries(filterState.sortOrderAttempted),
              user: {
                firstName: sortOrderCodeForQueries(filterState.sortOrder),
                lastName: sortOrderCodeForQueries(filterState.sortOrder),
              },
              ...(filterState.sortBySkill !== false && { skill: { id: 'ASC' } }),
              sessionCompletedOn: 'DESC',
            },
      ],
      skip: !firstLoad && !fetchingData,
      after: fetchingData && isMovingNext && pageInfo?.hasNextPage ? pageInfo?.endCursor : undefined,
      before: fetchingData && isMovingPrevious && pageInfo?.hasPreviousPage ? pageInfo?.startCursor : undefined,
    },
    { fetchPolicy: 'network-only', fetchKey: JSON.stringify(filterState) },
  );

  useEffect(() => {
    if (skillAsssessmentData.studentsSkillAssessments?.nodes) {
      setTotalRecords(skillAsssessmentData.studentsSkillAssessments.totalCount ?? 0);
      setPageInfo(skillAsssessmentData.studentsSkillAssessments.pageInfo);
      {
        setSkillAssessments(
          skillAsssessmentData.studentsSkillAssessments.nodes?.map(skillAsssessment => ({
            id: skillAsssessment.id || '',
            userId: skillAsssessment.user?.id || '',
            skill: skillAsssessment.skill || null,
            studentName: skillAsssessment.user?.lastName + ', ' + skillAsssessment.user?.firstName,
            firstName: skillAsssessment.user?.firstName || '',
            lastName: skillAsssessment.user?.lastName || '',
            publicUserId: skillAsssessment.user?.publicUserId || '',
            skillName: skillAsssessment.skill?.name || '',
            score: skillAsssessment.score || '',
            max_score: skillAsssessment.maxScore || '',
            completedOn: skillAsssessment.sessionCompletedOn || '',
            attempted: skillAsssessment.sessionNumber,
          })) ?? [],
        );
      }
      setFetchingData(false);
      setFirstLoad(false);
    }
    setFetchingData(false);
  }, [skillAsssessmentData]);

  return <div style={{ display: 'none' }}></div>;
};
